import type { StateCreator } from 'zustand/vanilla'

/**
 * BigInt middleware
 *
 * fixes issue with store values of type BigInt created with proto Timestamp
 * by parsing BigInt values to string and back
 *
 * Note: it would break File objects, so we skip them
 *
 * @credits: https://chatgpt.com/share/67db517c-afb0-8004-8e25-453b39be51f5 (tried an optimized version but no luck)
 */
export const bigIntMiddleware =
  <T extends object>(config: StateCreator<T>): StateCreator<T> =>
  (set, get, api) => {
    const serializeBigInt = (value: unknown): unknown => {
      if (typeof value === 'bigint') return value.toString()
      if (value instanceof File) return value // 🔥 Skip File objects
      if (Array.isArray(value)) return value.map(serializeBigInt)
      if (typeof value === 'object' && value !== null) {
        return Object.fromEntries(Object.entries(value).map(([key, val]) => [key, serializeBigInt(val)]))
      }
      return value
    }

    const deserializeBigInt = (value: unknown): unknown => {
      if (typeof value === 'string' && /^\d+n?$/.test(value)) return BigInt(value.replace('n', ''))
      if (Array.isArray(value)) return value.map(deserializeBigInt)
      if (typeof value === 'object' && value !== null) {
        return Object.fromEntries(Object.entries(value).map(([key, val]) => [key, deserializeBigInt(val)]))
      }
      return value
    }

    return config(
      (partial, replace) => {
        const newState = typeof partial === 'function' ? partial(get()) : partial
        set(serializeBigInt(newState) as T, replace as any)
      },
      () => deserializeBigInt(get()) as T,
      api,
    )
  }
